'use client';

import { FormEvent, useEffect, useState } from 'react';
import {
  addStorefrontPage,
  deleteStorefrontPage,
  getConnectStatus,
  getVendorSettings,
  listStorefrontPages,
  startConnectOnboarding,
  updateVendorSettings,
  type StorefrontPage,
  type VendorSettings,
} from './api';
import { vendorPolicyReminder } from '@/platform/legal/marketplacePolicy';

export interface VendorStorePanelProps {
  vendorUserId: string;
  readOnly?: boolean;
}

export function VendorStorePanel({ vendorUserId, readOnly = false }: VendorStorePanelProps) {
  const [settings, setSettings] = useState<VendorSettings>({
    storeName: '',
    tagline: '',
    supportEmail: '',
    shippingPolicy: '',
    returnPolicy: '',
    storePublic: true,
  });
  const [pages, setPages] = useState<StorefrontPage[]>([]);
  const [connectComplete, setConnectComplete] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getVendorSettings(vendorUserId)
      .then((s) => setSettings((prev) => ({ ...prev, ...s })))
      .catch(() => undefined);
    listStorefrontPages(vendorUserId)
      .then((r) => setPages(r.pages))
      .catch(() => undefined);
    getConnectStatus(vendorUserId)
      .then((s) => setConnectComplete(s.connectOnboardingComplete))
      .catch(() => undefined);
  }, [vendorUserId]);

  async function saveSettings(e: FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const saved = await updateVendorSettings(vendorUserId, settings);
      setSettings(saved);
      setMessage('Store settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings');
    }
  }

  async function addPage(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const page = await addStorefrontPage(vendorUserId, url, label);
      setPages((prev) => [page, ...prev]);
      setUrl('');
      setLabel('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add storefront URL');
    }
  }

  async function removePage(pageId: string) {
    await deleteStorefrontPage(vendorUserId, pageId);
    setPages((prev) => prev.filter((p) => p.id !== pageId));
  }

  async function openConnect() {
    setError('');
    try {
      const { onboardingUrl } = await startConnectOnboarding(vendorUserId);
      window.location.href = onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open payment setup');
    }
  }

  return (
    <section className="vendor-console__form-grid">
      <h2 className="vendor-console__section-title">Payment setup</h2>
      <p className="vendor-console__hint">
        Stripe Connect Express receives your payouts. Required before hosted checkout can go live.
      </p>
      <p>
        Status:{' '}
        <strong>{connectComplete ? 'Ready to accept payments' : 'Setup incomplete'}</strong>
      </p>
      {!connectComplete && (
        <button type="button" className="btn btn-primary" onClick={() => void openConnect()}>
          Complete Stripe Connect setup
        </button>
      )}

      <h2 className="vendor-console__section-title">Store settings</h2>
      <p className="vendor-console__hint">{vendorPolicyReminder}</p>
      {message && <p className="text-muted">{message}</p>}
      {error && <p className="form-error">{error}</p>}
      <fieldset disabled={readOnly}>
      <form onSubmit={(e) => void saveSettings(e)}>
        <div className="vendor-console__form-grid">
          <label>
            Store name
            <input
              value={settings.storeName}
              onChange={(e) => setSettings({ ...settings, storeName: e.target.value })}
              required
            />
          </label>
          <label>
            Tagline
            <input
              value={settings.tagline ?? ''}
              onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
              placeholder="Short description for buyers"
            />
          </label>
          <label>
            Support email
            <input
              type="email"
              value={settings.supportEmail ?? ''}
              onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
            />
          </label>
          <label>
            Shipping policy
            <textarea
              value={settings.shippingPolicy ?? ''}
              onChange={(e) => setSettings({ ...settings, shippingPolicy: e.target.value })}
              rows={3}
            />
          </label>
          <label>
            Return policy
            <textarea
              value={settings.returnPolicy ?? ''}
              onChange={(e) => setSettings({ ...settings, returnPolicy: e.target.value })}
              rows={3}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.storePublic}
              onChange={(e) => setSettings({ ...settings, storePublic: e.target.checked })}
            />{' '}
            Show store and products on public profile
          </label>
        </div>
        <div className="vendor-console__submit settings-page__submit">
          <button type="submit" className="btn btn-primary">
            Save store settings
          </button>
        </div>
      </form>

      <h3>External storefront URLs</h3>
      <p className="text-muted">Optional links to your other shops (Etsy, eBay, etc.). Checkout uses hosted payments here.</p>
      <form onSubmit={(e) => void addPage(e)} className="vendor-console__form-grid">
        <label>
          Shop URL
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://etsy.com/shop/..."
            required
          />
        </label>
        <label>
          Label (optional)
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Etsy shop" />
        </label>
        <div className="vendor-console__submit">
          <button type="submit" className="btn btn-secondary">
            Add storefront URL
          </button>
        </div>
      </form>
      <ul className="vendor-storefronts__list">
        {pages.map((p) => (
          <li key={p.id}>
            <a href={p.url} target="_blank" rel="noopener noreferrer">
              {p.label || p.domain}
            </a>{' '}
            <button type="button" className="btn btn-link" onClick={() => void removePage(p.id)} disabled={readOnly}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      </fieldset>
    </section>
  );
}
